import React, { useState } from 'react';
import OutlinedTextField from 'components/forms/textFields/OutlinedTextField/OutlinedTextField';
import { FormattedMessage } from 'react-intl';
import Key from 'types/Key';

function month(value: string) {
  const numbers = value.replace(/\D/g, '');

  if (value && value[0] !== '0' && value[0] !== '1') {
    return '0'.concat(numbers);
  }

  return value.replace(/\D/g, '');
}

function char(char: string, value: string, start: number) {
  if (value.length < start) {
    return value;
  }

  const substring = value.substring(start);

  return value.substring(0, start).concat(substring !== char ? char + substring : substring);
}

function year(value: string, start: number) {
  const year = value.substring(start);
  return value.substring(0, start).concat(year.length === 4 ? year.slice(-2) : year.substring(0, 2));
}

function autoCorrect(value: string) {
  const a = month(value);
  const b = char(' ', a, 2);
  const c = char('/', b, 3);
  const d = char(' ', c, 4);
  const e = year(d, 5);
  return e;
}

type Props = {
  enterKeyHandler: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error: React.ReactNode;
  onChange: (e: any) => void;
  setError: (error: React.ReactNode) => void;
  value: string;
};

export default function CreditCardExpiration({ enterKeyHandler, error, onChange, setError, value }: Props) {
  const [lastKey, setLastKey] = useState<Key>();

  return (
    <OutlinedTextField
      enterKeyHandler={enterKeyHandler}
      autocomplete="cc-exp"
      type="text"
      inputMode="numeric"
      name="expiration"
      label={<FormattedMessage defaultMessage="MM / YY" />}
      aria-invalid="false"
      changeHandler={e => {
        if (lastKey === Key.Backspace) {
          const value = e.target.value;
          onChange({ target: { name: e.target.name, value: value.endsWith(' / ') ? value.substring(0, value.length - 3) : value } });
        } else {
          const value = autoCorrect(e.target.value);
          onChange({ target: { name: e.target.name, value } });
        }
      }}
      value={value}
      errorMessage={error}
      onBlur={e => {
        const { value } = e.target;

        // backspace
        if (value === '') {
          setError('');
          return;
        }

        const [month, year] = value.split(' / ');

        const date = new Date();

        if (!year || year.length === 1) {
          setError(<FormattedMessage defaultMessage="Your card's expiration date is incomplete." />);
          return;
        }

        if (year.length === 2) {
          if (Number.parseInt(year) < date.getFullYear() - 2000) {
            setError(<FormattedMessage defaultMessage="Your card's expiration date is invalid." />);
            return;
          }

          // using Stripe's 50 year maximum logic
          if (Number.parseInt('20'.concat(year)) - date.getFullYear() > 50) {
            setError(<FormattedMessage defaultMessage="Your card's expiration year is invalid." />);
            return;
          }
        }

        if (Number.parseInt(year) === date.getFullYear() - 2000 && Number.parseInt(month) < date.getMonth() + 1) {
          setError(<FormattedMessage defaultMessage="Your card's expiration date is in the past." />);
          return;
        }

        setError('');
      }}
      onKeyDown={e => {
        setLastKey(e.key as Key);
      }}
      onPaste={e => {
        e.preventDefault();
        const value = autoCorrect(e.clipboardData.getData('Text'));
        onChange({ target: { name: e.target.name, value } });
      }}
    />
  );
}
